import { useContext, useEffect, useState } from "react";
import DashboardLayoutMain from "../../../layout/dashboard/DashboardLayoutMain";
import "../../../styles/dashboard/transaction/transactionIndex.css";
import TitleBtnFragment from "../../../components/fragment/title-btn/TitleBtnFragment";
import {
  RazorBadgeTwo,
  RazorDateTime,
  RazorInputField,
  RazorNumberFormat,
  RazorTable,
  RazorTableRow,
  RazorTextTrim,
  RazorTwinValue,
} from "../../../components/razor-reuse";
import FilterReusableComp from "../../../components/FilterReusableComp";
import PaginationFragment from "../../../components/fragment/pagination/PaginationFragment";
import { useDispatch, useSelector } from "react-redux";
import RootState, { StripeTransactionItem } from "../../../redux/type";
import { getStripeTransaction } from "../../../redux/transaction";
import DateRangeComponent from "../../../components/fragment/date-range/DateRangeComponent";
import { useNavigate } from "react-router-dom";
import { ALL_ROUTES } from "../../../routes/AllRoutes";
import { reactSelectStyleTable } from "../../../utils/Helper";
import { systemRoles } from "../../../utils/roles";
import ActionContext from "../../../context/ActionContext";
import StripeTransactionModal from "./StripeTransactionModal";

const SESSION_STATUS_OPTIONS = [
  { label: "Initialized", value: "initialized" },
  { label: "Requires Payment", value: "requires_payment" },
  { label: "Fulfillment Processing", value: "fulfillment_processing" },
  { label: "Fulfillment Complete", value: "fulfillment_complete" },
  { label: "Rejected", value: "rejected" },
  { label: "All Status", value: "" },
];

const CREDIT_STATUS_OPTIONS = [
  { label: "Credited", value: "credited" },
  { label: "Not Credited", value: "not_credited" },
  { label: "Transfer Failed", value: "transfer_failed" },
  { label: "All Credit Status", value: "" },
];

const SESSION_STATUS_BADGE: Record<string, string> = {
  fulfillment_complete: "success",
  completed: "success",
  rejected: "failed",
  initialized: "pending",
  requires_payment: "pending",
  fulfillment_processing: "pending",
};

// business_transfer.status isn't a fixed enum we've been given, so bucket by
// keyword rather than an exact-match list that'd miss values we haven't seen.
function getBusinessTransferBadge(status?: string) {
  if (!status) return "";
  const normalized = status.toLowerCase();
  if (normalized.includes("fail") || normalized.includes("reject"))
    return "failed";
  if (
    normalized.includes("complete") ||
    normalized.includes("credited") ||
    normalized.includes("success")
  )
    return "success";
  return "pending";
}

function formatLabel(value?: string) {
  return value ? value.replace(/_/g, " ") : "---";
}

const StripeTransaction = () => {
  const actionCtx = useContext(ActionContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { user } = useSelector((state: RootState) => state.auth);
  const { all_currencies_drop } = useSelector(
    (state: RootState) => state.settings,
  );
  const { loading_stripe, stripe_transaction } = useSelector(
    (state: RootState) => state.transaction,
  );

  const headerList = [
    "Customer",
    "Session ID",
    "Card Payment",
    "Crypto Delivered",
    "Status",
    "Date",
    "Action",
  ];

  const currencyOptions = [
    ...(all_currencies_drop || [])
      .filter((currency: any) => currency?.code)
      .map((currency: any) => ({
        label: currency.code,
        value: currency.code,
      })),
    { label: "All Currencies", value: "" },
  ];

  const [statusFilter, setStatusFilter] = useState<any>("");
  const [creditStatusFilter, setCreditStatusFilter] = useState<any>("");
  const [currencyFilter, setCurrencyFilter] = useState<any>("");
  const [dateRange, setDateRange] = useState("");
  const [search, setSearch] = useState("");
  const [currentPageVal, setCurrentPage] = useState(1);
  const [activeRow, setActiveRow] = useState<StripeTransactionItem | null>(
    null,
  );

  // useEffect to check if user has permission to view transactions
  useEffect(() => {
    if (
      !user?.permissions?.includes(systemRoles[11]) &&
      user?.permissions?.length > 0
    ) {
      navigate(ALL_ROUTES?.DASHBOARD_OVERVIEW);
    }
  }, [user?.permissions]);

  const obj = {
    page: currentPageVal,
    per_page: actionCtx?.perPage?.label,
    search,
    status: statusFilter?.value,
    credit_status: creditStatusFilter?.value,
    currency: currencyFilter?.value,
    "filter[date]": dateRange,
  };
  useEffect(() => {
    if (user?.permissions?.includes(systemRoles[11])) {
      dispatch(getStripeTransaction(obj) as any);
    }
  }, [
    currentPageVal,
    dateRange,
    statusFilter,
    creditStatusFilter,
    currencyFilter,
    actionCtx?.perPage,
    dispatch,
    user?.permissions,
    search,
  ]);

  const rows: StripeTransactionItem[] = stripe_transaction?.data || [];

  return (
    <>
      <DashboardLayoutMain
        loading={loading_stripe && rows.length < 1}
        title="Stripe Transactions"
      >
        <div className="transaction-index-wrap-box-smartsend">
          <FilterReusableComp
            defaultSelectOne="Session Status"
            selectOneList={SESSION_STATUS_OPTIONS}
            selectOneValue={statusFilter}
            onSelectOne={(param) => setStatusFilter(param)}
            defaultSelectTwo="Credit Status"
            selectTwoList={CREDIT_STATUS_OPTIONS}
            selectTwoValue={creditStatusFilter}
            onSelectTwo={(param) => setCreditStatusFilter(param)}
            hideExport
            searchPlaceholder="Search by session ID or customer"
            onSearch={(param) => setSearch(param)}
            moreComponents={
              <>
                <RazorInputField
                  color="black-light"
                  type="select"
                  className="select-input"
                  selectStyles={reactSelectStyleTable}
                  selectOption={currencyOptions}
                  value={
                    currencyFilter || { label: "All Currencies", value: "" }
                  }
                  onChange={(e: any) => setCurrencyFilter(e)}
                  placeholder="Currency"
                />
                <DateRangeComponent
                  onSet={(param) => {
                    setDateRange(param);
                  }}
                  value={dateRange}
                  onCancel={() => {
                    setDateRange("");
                  }}
                />
              </>
            }
          />
          {/* filter section end */}
          {/* table start */}
          <div className="table-section-box">
            <RazorTable headerList={headerList}>
              {rows.map((chi, idx) => {
                const customerName = chi?.customer
                  ? `${chi.customer.first_name || ""} ${chi.customer.last_name || ""}`.trim() ||
                    chi.customer.email
                  : "---";
                return (
                  <RazorTableRow
                    key={chi?.id || idx}
                    onRowClick={() => setActiveRow(chi)}
                    one={
                      <RazorTwinValue
                        one={RazorTextTrim(customerName, 30)}
                        two={RazorTextTrim(chi?.customer?.email || "", 30)}
                      />
                    }
                    two={RazorTextTrim(chi?.stripe_session_id || "----", 22)}
                    three={
                      <RazorTwinValue
                        one={RazorNumberFormat(chi?.card_payment?.amount, {
                          hideSymbol: false,
                          symbol: chi?.card_payment?.currency,
                        })}
                        two={`Total ${RazorNumberFormat(
                          chi?.card_payment?.total_charged,
                          {
                            hideSymbol: false,
                            symbol: chi?.card_payment?.currency,
                          },
                        )}`}
                      />
                    }
                    four={
                      <RazorTwinValue
                        one={`${chi?.crypto_delivery?.amount || "0"} ${
                          chi?.crypto_delivery?.asset?.toUpperCase() || ""
                        }`}
                        two={chi?.crypto_delivery?.network || ""}
                      />
                    }
                    five={
                      <RazorBadgeTwo
                        className={
                          SESSION_STATUS_BADGE[chi?.status || ""] || "pending"
                        }
                        text={formatLabel(chi?.status)}
                      />
                    }
                    loading={loading_stripe}
                    six={<RazorDateTime date={chi?.created_at} />}
                    seven={"View Details"}
                  />
                );
              })}
            </RazorTable>
          </div>
          {/* table end */}
          {/* pagination start */}
          <PaginationFragment
            currentPage={stripe_transaction?.meta?.current_page || 1}
            perPage={stripe_transaction?.meta?.per_page}
            totalPage={stripe_transaction?.meta?.total}
            onPageChange={(num) => {
              setCurrentPage(num);
            }}
            hidePagination={loading_stripe}
            showPerPage
          />
          {/* pagination end */}
        </div>
      </DashboardLayoutMain>

      <StripeTransactionModal
        visible={!!activeRow}
        details={activeRow}
        onClose={() => setActiveRow(null)}
      />
    </>
  );
};

export default StripeTransaction;
